<script setup lang="ts">
import { MAX_DEFAULT_BUDGET, MIN_DEFAULT_BUDGET } from '@/helpers/priceHelper'
import type { ReplicasValues } from '~/types/search/types'

const { nbHits, template } = defineProps<{
  hideSorter?: boolean
  nbHits: number
  template: 'list' | 'map' | 'split'
}>()
const emits = defineEmits<{
  'change-template': [value: 'list' | 'split' | 'map']
  'change-replica': [{ label: ReplicasValues; value: string }]
  'open-filters': []
  'update-budget': [string, string]
  'update-rooms': [{ bathrooms: number; bedrooms: number }]
}>()

const { t } = useI18n({ useScope: 'local' })
const {
  trackDropdownSticky,
  trackFiltersModal,
  trackMapReduced,
  trackSearchMap,
} = useSearchTracking()

const { filtersCount } = useSearchFiltersCount()
const { maxBudget, minBudget, hasBudget } = useSearchBudgetFilters()
const { bathrooms, bedrooms } = useSearchNumericFilters()
const { budgetFormatted, roomsFormatted } = useSearchClientHeader()

const hasRooms = computed(() => bathrooms.value > 0 || bedrooms.value > 0)

const updateBudget = (max: string, min: string, callback: () => void) => {
  callback()
  emits('update-budget', max, min)
}

const updateRooms = (
  rooms: { bathrooms: number; bedrooms: number },
  callback: () => void,
) => {
  callback()
  emits('update-rooms', rooms)
}

const openFilters = () => {
  trackFiltersModal(template)
  emits('open-filters')
}

const trackingDropdown = (type: 'rooms' | 'budget', callback: () => void) => {
  trackDropdownSticky(type, template)
  callback()
}

const mapReducedHandler = () => {
  trackMapReduced()
  emits('change-template', 'split')
}

const mapOpenedHandler = () => {
  trackSearchMap({ event: 'map_open' })
  emits('change-template', 'split')
}
</script>

<template>
  <div
    :class="[
      'sticky top-[72px] z-30 flex flex-col gap-4 p-4 lg:px-6',
      { 'bg-white': template !== 'map' },
    ]"
  >
    <div class="flex justify-between">
      <div class="flex gap-2">
        <button
          v-if="template !== 'list'"
          type="button"
          class="flex items-center gap-2 rounded border border-gray-200 bg-white px-3 py-2 text-md font-bold"
          @click="openFilters"
        >
          <BaseIcon name="filterLc" aria-hidden="true" focusable="false" />
          {{ t('allFilters') }}
          <div
            v-if="filtersCount"
            class="flex size-[20px] items-center justify-center rounded-full bg-secondary-100 text-3xs font-bold"
          >
            {{ filtersCount }}
          </div>
        </button>
        <SearchDropdown>
          <template #button="{ toggleDropdown, open }">
            <SearchResultHeaderV2DropdownButton
              :empty="!hasRooms"
              :open="open"
              class="p-4"
              @clear="$emit('update-rooms', { bedrooms: 0, bathrooms: 0 })"
              @click="trackingDropdown('rooms', toggleDropdown)"
            >
              {{ hasRooms ? roomsFormatted : t('rooms') }}
            </SearchResultHeaderV2DropdownButton>
          </template>
          <template #content="{ toggleDropdown }">
            <SearchResultHeaderV2DropdownRooms
              :bathrooms="bathrooms"
              :bedrooms="bedrooms"
              class="w-[298px] p-4"
              @update-rooms="updateRooms($event, toggleDropdown)"
            />
          </template>
        </SearchDropdown>

        <SearchDropdown>
          <template #button="{ toggleDropdown, open }">
            <SearchResultHeaderV2DropdownButton
              :empty="!hasBudget"
              :open="open"
              @clear="
                $emit('update-budget', MAX_DEFAULT_BUDGET, MIN_DEFAULT_BUDGET)
              "
              @click="trackingDropdown('budget', toggleDropdown)"
            >
              {{ hasBudget ? budgetFormatted : t('budget') }}
            </SearchResultHeaderV2DropdownButton>
          </template>
          <template #content="{ toggleDropdown }">
            <SearchResultHeaderV2DropdownBudget
              class="w-[272px] p-4"
              :max-budget="maxBudget"
              :min-budget="minBudget"
              @update-budget="
                (min, max) => updateBudget(min, max, toggleDropdown)
              "
            />
          </template>
        </SearchDropdown>
      </div>

      <BaseButton
        v-if="template === 'list'"
        class="ml-4 !px-3 !py-2 !text-md !font-semibold"
        color="secondary"
        has-icon
        variant="btn"
        @click="mapOpenedHandler"
      >
        <BaseIcon
          aria-hidden="true"
          class="mr-1"
          focusable="false"
          name="map"
        />
        {{ t('seeOnMap') }}
      </BaseButton>
      <button
        v-else-if="template === 'map'"
        class="flex items-center gap-2 rounded border border-gray-200 bg-white px-3 py-2 text-xs font-medium lg:text-md"
        type="button"
        @click="mapReducedHandler"
      >
        <BaseIcon name="collapse" :size="1" />
        {{ t('collapse') }}
      </button>
    </div>

    <div
      v-if="template !== 'map' && !hideSorter"
      class="flex items-center gap-2"
    >
      <div
        :class="[
          'text-xs font-semibold',
          { 'animate-pulse bg-gray-200 text-gray-200': nbHits === 0 },
        ]"
      >
        {{ $t('search.propertyCount', nbHits) }}
      </div>
      <div class="h-[11px] border-l border-gray-700" />
      <SearchResultHeaderSorter
        @change-replica="$emit('change-replica', $event)"
      />
    </div>
  </div>
</template>

<i18n lang="json">
{
  "en": {
    "allFilters": "All filters",
    "budget": "Budget",
    "collapse": "Reduce map view",
    "rooms": "Rooms",
    "seeOnMap": "Map view"
  },
  "fr": {
    "allFilters": "Tous les filtres",
    "budget": "Budget",
    "collapse": "Réduire la vue carte",
    "rooms": "Pièces",
    "seeOnMap": "Vue carte"
  }
}
</i18n>

<style scoped>
:deep(.multiselect-replica .multiselect) {
  @apply border-0;
  min-height: auto;
}
:deep(.multiselect-replica .multiselect-placeholder),
:deep(.multiselect-replica .multiselect-single-label) {
  @apply static	px-2;
}
:deep(.multiselect-replica .multiselect-caret) {
  @apply m-0;
}
</style>
